"use client";

import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { createZipSession, type ZipSessionOptions } from "../../services/zip/zip-session";
import { zipStore, type ZipStore, type ZipStoreConfig } from "../../services/zip/zip-store";

/*
 * "use client": hooks over `useSyncExternalStore`, so these only ever run in a
 * client component. `services/zip` stays plain TypeScript and imports no React;
 * this file is where these hooks bind it to React.
 */

/* Layout effects warn on the server; the server never needs this one. */
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** The page-wide ZIP, fee and counties. Empty on the server and on the first client render. */
export function useZipStore(store: ZipStore = zipStore) {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);
}

/**
 * One ZIP Code's working state. The session is created once per mount and its
 * options are refreshed every render, so a host that passes fresh callbacks
 * never restarts a timer (handed over in a layout effect, before the start
 * effect, so no render writes to it). It starts syncing with the store in an
 * effect, so nothing runs on the server. The store binds on FIRST mount: a
 * different `store` argument later is ignored.
 */
export function useZipSession(
  options: ZipSessionOptions,
  config: ZipStoreConfig = {},
  store: ZipStore = zipStore
) {
  const [session] = useState(() => createZipSession(store, options));
  useIsomorphicLayoutEffect(() => {
    session.setOptions(options);
  });

  const { channelLookupPath, offersPath, localChannelsBasePath } = config;
  useEffect(
    () => session.start({
      channelLookupPath,
      offersPath,
      localChannelsBasePath
    }),
    // The paths are fixed for a page; a change re-starts the session against the new ones.
    [session, channelLookupPath, offersPath, localChannelsBasePath]
  );

  const state = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getServerSnapshot);
  return {
    session,
    state
  };
}
